/**
 * The shape of the "Prévia" thumbnail.
 *
 * The preview exists to answer one question — what will be saved — so it has to be the same shape
 * as what will be saved. It was not: an identity photo rendered it through an MUI `Avatar`, which
 * masks to a circle and centre-crops whatever it is handed. Draw a free selection on an identity
 * photo and the preview showed a circle cut out of the MIDDLE of that selection — a different
 * region from the one marked on the photo, with nothing to say it was only a mask.
 *
 * The rectangular branch had the same bug in milder form: it sized itself from the configured
 * `aspectRatio` and ignored the ratio the operator had just drawn.
 */

export interface PreviewBox {
  kind: 'circle' | 'rect';
  width: number;
  height: number;
}

/** The longest edge a thumbnail may occupy in the control panel. */
const MAX_EDGE = 90;
const CIRCLE_SIZE = 60;

export function previewBoxFor(input: {
  cropShape?: 'round' | 'rect';
  /** The configured ratio for this kind of photo. */
  aspectRatio?: number;
  /** The ratio the operator drew with "Selecionar Área", when they drew one. */
  selectionAspect?: number;
}): PreviewBox {
  const selection = input.selectionAspect;
  const hasSelection = typeof selection === 'number' && Number.isFinite(selection) && selection > 0;

  // A free selection is a rectangle whatever the crop shape is; masking it to a circle would show
  // a different region from the one that was marked.
  if (input.cropShape === 'round' && !hasSelection) {
    return { kind: 'circle', width: CIRCLE_SIZE, height: CIRCLE_SIZE };
  }

  const aspect = hasSelection ? (selection as number) : (input.aspectRatio ?? 3 / 2);

  // Sized off the LONG edge, so a 6:1 plate does not run off a 300px column and a tall strip does
  // not grow instead.
  const width = aspect >= 1 ? MAX_EDGE : Math.max(1, Math.round(MAX_EDGE * aspect));
  const height = aspect >= 1 ? Math.max(1, Math.round(MAX_EDGE / aspect)) : MAX_EDGE;

  return { kind: 'rect', width, height };
}
